import {
    Content,
    Group,
    Icon,
    Item,
    ItemIndicator,
    ItemText,
    Portal,
    Root,
    Separator,
    Trigger,
    Value,
    Viewport,
} from '@radix-ui/react-select';
import clsx from 'clsx';

export { Content, Group, Icon, Item, ItemIndicator, ItemText, Portal, Root, Separator, Trigger, Value, Viewport };

import type { ComponentProps, FunctionComponent, ReactNode } from 'react';

import { IconChevronGrabberVertical } from '../icons';
import { CheckIcon } from '../icons/CheckIcon';
import { floatingSurfaceClassNames, floatingSurfaceItemClassNames } from '../surface/surfaceClasses';

export type SelectProps = {
    readonly 'aria-label'?: string;
    readonly children: ReactNode;
    readonly defaultValue?: string;
    readonly disabled?: boolean;
    readonly onValueChange?: (value: string) => void;
    readonly placeholder?: string;
    readonly value?: string;
    readonly variant?: 'default' | 'error' | 'success';
};

export type SelectItemProps = {
    readonly children: ReactNode;
    readonly className?: string;
    readonly disabled?: boolean;
    readonly value: string;
};

export type SelectGroupProps = {
    readonly children: ReactNode;
    readonly label?: string;
};

export type SelectSeparatorProps = {
    readonly className?: string;
};

const variantBorderClasses = {
    default: 'border-gray-300',
    error: 'border-error-400',
    success: 'border-success-400',
} as const;

const variantFocusClasses = {
    default: 'focus:border-gray-700',
    error: 'focus:border-error-600',
    success: 'focus:border-success-600',
} as const;

export const StyledContent: FunctionComponent<ComponentProps<typeof Content>> = ({ className, ...props }) => (
    <Content
        className={clsx(floatingSurfaceClassNames.tone.white, 'rounded-md overflow-hidden z-[1205]', className)}
        {...props}
    />
);

export const StyledViewport: FunctionComponent<ComponentProps<typeof Viewport>> = ({ className, ...props }) => (
    <Viewport className={clsx('bg-white-200 p-xs', className)} {...props} />
);

export const StyledItem: FunctionComponent<ComponentProps<typeof Item> & SelectItemProps> = ({
    className,
    ...props
}) => (
    <Item
        className={clsx(
            'flex flex-row items-start gap-s rounded-sm relative select-none',
            'text-xxs font-normal leading-[1rem] text-gray-900',
            'py-s px-m cursor-pointer',
            floatingSurfaceItemClassNames.hover,
            'data-[disabled]:text-gray-400 data-[disabled]:cursor-default data-[disabled]:pointer-events-none',
            floatingSurfaceItemClassNames.selectHighlighted,
            className,
        )}
        {...props}
    />
);

export const Select: FunctionComponent<SelectProps> = ({
    'aria-label': ariaLabel,
    children,
    defaultValue,
    disabled = false,
    onValueChange,
    placeholder,
    value,
    variant = 'default',
    ...props
}) => {
    return (
        <Root defaultValue={defaultValue} disabled={disabled} value={value} onValueChange={onValueChange} {...props}>
            <Trigger
                aria-label={ariaLabel}
                className={clsx(
                    'flex items-center justify-between w-full',
                    'bg-white-200 border rounded-md outline-none',
                    'text-xxs leading-[1rem] font-sans font-normal tracking-tight text-gray-900',
                    'py-m px-l',
                    'transition-all duration-100 ease-in-out',
                    'hover:bg-gray-200',
                    'data-[disabled]:text-gray-400 data-[disabled]:cursor-default data-[disabled]:pointer-events-none',
                    'data-[placeholder]:text-gray-700',
                    variantBorderClasses[variant],
                    disabled ? 'cursor-default opacity-50' : clsx('cursor-pointer', variantFocusClasses[variant]),
                )}
            >
                <Value placeholder={placeholder} />
                <Icon className="flex text-gray-700 transition-transform duration-200 ease-in-out [&>svg]:h-[20px] [&>svg]:w-[20px]">
                    <IconChevronGrabberVertical />
                </Icon>
            </Trigger>
            <Portal>
                <StyledContent>
                    <StyledViewport>{children}</StyledViewport>
                </StyledContent>
            </Portal>
        </Root>
    );
};

export const SelectItem: FunctionComponent<SelectItemProps> = ({ children, disabled, value, ...props }) => {
    return (
        <StyledItem disabled={disabled} value={value} {...props}>
            <ItemText className="block flex-1">{children}</ItemText>
            <ItemIndicator className="inline-flex items-center justify-center text-gray-900 ml-s">
                <CheckIcon />
            </ItemIndicator>
        </StyledItem>
    );
};

export const SelectGroup: FunctionComponent<SelectGroupProps> = ({ children, label, ...props }) => {
    return (
        <Group
            className="data-[disabled]:text-gray-400 data-[disabled]:cursor-default data-[disabled]:pointer-events-none"
            {...props}
        >
            {label !== undefined && <div className="text-gray-700 text-xxs font-normal p-s">{label}</div>}
            {children}
        </Group>
    );
};

export const SelectSeparator: FunctionComponent<SelectSeparatorProps> = ({ className, ...props }) => {
    return <Separator className={clsx('bg-gray-300 h-px m-xs', className)} {...props} />;
};
